import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../components/shared/Button";
import Loader from "../../components/shared/Loader";
import Section from "../../layouts/Section";
import SectionBody from "../../layouts/SectionBody";
import {
  generateSitemap,
  getSitemapDownloadUrl,
  getSitemapJobs,
  retrySitemapJob,
} from "../../utils/Apis/sitemaps";

const STATUS_TEXT = {
  queued: "等待中",
  running: "生成中",
  completed: "已完成",
  failed: "失败",
};

/**
 * 格式化后台显示时间。
 * @param {string|undefined} value ISO 时间
 * @returns {string} 本地时间
 */
const formatDate = (value) =>
  value ? new Date(value).toLocaleString("zh-CN") : "-";

/**
 * Sitemap 生成与维护页面。
 * @returns {JSX.Element} 页面组件
 */
const Sitemaps = () => {
  const [baseUrl, setBaseUrl] = useState("https://www.creatyea.com");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /**
   * 刷新任务列表。
   * @param {boolean} showLoading 是否显示首次加载状态
   */
  const loadJobs = useCallback(async (showLoading = false) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const response = await getSitemapJobs();
      setJobs(response.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    document.title = "Sitemap 管理 - Admin";
    loadJobs(true);
    return () => {
      document.title = "Creatyea Technology | Admin";
    };
  }, [loadJobs]);

  const hasActiveJob = useMemo(
    () => jobs.some((job) => ["queued", "running"].includes(job.status)),
    [jobs],
  );

  useEffect(() => {
    if (!hasActiveJob) {
      return undefined;
    }
    const timer = window.setInterval(() => loadJobs(), 2000);
    return () => window.clearInterval(timer);
  }, [hasActiveJob, loadJobs]);

  /**
   * 提交新的生成任务。
   */
  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      const response = await generateSitemap({ baseUrl });
      toast.success(
        response.reused ? "已有任务正在执行" : "Sitemap 任务已创建",
      );
      await loadJobs();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * 从检查点重试失败任务。
   * @param {string} jobId 任务 ID
   */
  const handleRetry = async (jobId) => {
    try {
      await retrySitemapJob(jobId);
      toast.success("任务已从检查点重新启动");
      await loadJobs();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    }
  };

  return (
    <div>
      <Section title="Sitemap 管理" className="border-none lg:pb-1" />
      <SectionBody className="custom-scroll max-h-[75vh] overflow-auto">
        <div className="rounded-lg border border-primary/20 bg-white p-4">
          <h2 className="mb-2 text-lg font-semibold">生成 sitemap.xml</h2>
          <p className="mb-4 text-sm text-gray-600">
            商品将分批读取；超过单文件限制时自动生成 sitemap
            索引和分片文件。新版本完成前不会影响旧版本。
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder="https://www.creatyea.com"
            />
            <Button
              onClick={handleGenerate}
              disabled={submitting || hasActiveJob || !baseUrl.trim()}
              className="disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? "提交中..."
                : hasActiveJob
                  ? "正在生成"
                  : "开始生成"}
            </Button>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            定时维护可在后端配置 SITEMAP_AUTO_GENERATE_HOURS（例如
            24）；不配置时仅手动生成。
          </p>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-primary/20 bg-white">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3">创建时间</th>
                  <th className="px-3 py-3">状态</th>
                  <th className="px-3 py-3">进度</th>
                  <th className="px-3 py-3">站点地址</th>
                  <th className="px-3 py-3">文件</th>
                  <th className="px-3 py-3">操作/错误</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const fileNames =
                    job.status === "completed"
                      ? [
                          "sitemap.xml",
                          ...(job.files || [])
                            .map((file) => file.name)
                            .filter((name) => name !== "sitemap.xml"),
                        ]
                      : [];
                  return (
                    <tr key={job.id} className="border-t border-gray-100">
                      <td className="px-3 py-3">{formatDate(job.createdAt)}</td>
                      <td className="px-3 py-3">{STATUS_TEXT[job.status]}</td>
                      <td className="px-3 py-3">
                        {job.progress}% ({job.processedCount}/{job.totalCount})
                      </td>
                      <td className="px-3 py-3">{job.baseUrl}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-start gap-1">
                          {fileNames.map((fileName) => (
                            <a
                              key={fileName}
                              className="text-primary hover:underline"
                              href={getSitemapDownloadUrl(job.id, fileName)}
                            >
                              {fileName}
                            </a>
                          ))}
                          {fileNames.length === 0 && "-"}
                        </div>
                      </td>
                      <td className="max-w-[260px] px-3 py-3">
                        {job.status === "failed" ? (
                          <div className="flex flex-col gap-2">
                            <span className="break-words text-red-600">
                              {job.errorMessage || "未知错误"}
                            </span>
                            <Button
                              className="w-fit py-1 text-xs"
                              onClick={() => handleRetry(job.id)}
                              disabled={hasActiveJob}
                            >
                              从断点重试
                            </Button>
                          </div>
                        ) : job.completedAt ? (
                          formatDate(job.completedAt)
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
                {jobs.length === 0 && (
                  <tr>
                    <td className="px-3 py-8 text-center" colSpan="6">
                      暂无生成记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </SectionBody>
    </div>
  );
};

export default Sitemaps;
